import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    EmptyState,
    ErrorState,
    PaginationControls,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { OpenRecordButton } from "@/components/ui/open-record-button";
import { PAGINATION } from "@/constants/pagination";
import { PERMISSIONS } from "@/constants/permissions";
import { useGetContacts } from "@/queries/contact.queries";
import { createPaginationProps } from "@/types/pagination.types";
import { formatDateRelative } from "@/utils/format-date";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

const SKELETON_ROWS = 8;
const COLUMN_COUNT = 5;

const ContactsSearchSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_CONTACTS)({
    component: ContactsPage,
    validateSearch: searchValidator(ContactsSearchSchema),
    staticData: {
        config: {
            title: "developer_contacts_title",
            breadcrumb: {
                title: "developer_contacts_title",
                to: APP_ROUTES_TO.DEVELOPER_CONTACTS,
            },
        },
        permissions: [PERMISSIONS.CALL_CONTACTS_READ],
    },
});

function ContactsTableSkeleton() {
    return Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <TableRow key={index}>
            {Array.from({ length: COLUMN_COUNT }, (_, cellIndex) => (
                <TableCell key={cellIndex}>
                    <Skeleton className="h-4 w-full" />
                </TableCell>
            ))}
        </TableRow>
    ));
}

function ContactsPage() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const page = Route.useSearch().page ?? PAGINATION.DEFAULT_PAGE;

    const { data, isLoading, isFetching, isError } = useGetContacts({ page, limit: PAGINATION.PAGE_SIZE });
    const contacts = data?.data ?? [];
    const total = data?.pagination.total ?? 0;

    const setPage = (nextPage: number) => {
        navigate({ search: (prev) => ({ ...prev, page: nextPage }), replace: true });
    };

    const paginationProps = createPaginationProps({ response: data, onPageChange: setPage, isLoading: isFetching });

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2">
                    <span>{t("developer_contacts_title")}</span>
                    {data ? (
                        <span className="text-sm font-normal text-muted-foreground">
                            {t("developer_contacts_total", { count: total })}
                        </span>
                    ) : null}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {isError ? <ErrorState title={t("developer_contacts_loading_error")} /> : null}
                {!isError && !isLoading && contacts.length === 0 ? (
                    <EmptyState variant="simple" title="developer_contacts_no_data" />
                ) : null}
                {!isError && (isLoading || contacts.length > 0) ? (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t("name")}</TableHead>
                                    <TableHead>{t("email")}</TableHead>
                                    <TableHead>{t("phone")}</TableHead>
                                    <TableHead>{t("updated_at")}</TableHead>
                                    <TableHead className="text-right">{t("developer_contacts_zoho_id")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? <ContactsTableSkeleton /> : null}
                                {contacts.map((contact) => (
                                    <TableRow key={contact.id}>
                                        <TableCell className="font-medium whitespace-nowrap">{contact.name}</TableCell>
                                        <TableCell className="whitespace-nowrap">{contact.email}</TableCell>
                                        <TableCell className="font-mono text-sm whitespace-nowrap">
                                            {contact.phone}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-muted-foreground">
                                            {formatDateRelative(contact.updatedAt)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <OpenRecordButton
                                                id={contact.zohoContactId}
                                                moduleType="Contacts"
                                                size="sm"
                                                label={
                                                    <span className="font-mono text-xs">{contact.zohoContactId}</span>
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                ) : null}

                {paginationProps.totalPages > 1 ? (
                    <PaginationControls
                        currentPage={page}
                        totalPages={paginationProps.totalPages}
                        onPageChange={setPage}
                        isLoading={paginationProps.isLoading}
                        size="sm"
                    />
                ) : null}
            </CardContent>
        </Card>
    );
}
